import * as AdaptiveCards from 'adaptivecards';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import * as PropTypes from 'prop-types';
import MarkdownIt from "markdown-it";

export interface Props {
    payload: object;
    onExecuteAction?: Function;
    onActionSubmit?: Function;
    onActionOpenUrl?: Function;
    onActionShowCard?: Function;
    onError?: Function;
    style?: object;
    hostConfig?: object;
}

const propTypes = {
    /** The card schema.  It must comply with the card schema. [More Info](https://docs.microsoft.com/en-us/adaptive-cards/create/cardschema) */
    payload: PropTypes.object.isRequired,
    /** Method that will be invoked anytime a card action is executed. [More Info](https://docs.microsoft.com/en-us/adaptive-cards/display/implementingrenderer#actions) */
    onExecuteAction: PropTypes.func,
    /** Method that will be invoked when a Submit action is executed. [More Info](https://docs.microsoft.com/en-us/adaptive-cards/display/implementingrenderer#actionsubmit) */
    onActionSubmit: PropTypes.func,
    /** Method that will be invoked when an Open Url action is executed. [More Info](https://docs.microsoft.com/en-us/adaptive-cards/display/implementingrenderer#actionopenurl) */
    onActionOpenUrl: PropTypes.func,
    /** Method that will be invoked when a Show Card action is executed. [More Info](https://docs.microsoft.com/en-us/adaptive-cards/display/implementingrenderer#actionshowcard) */
    onActionShowCard: PropTypes.func,
    /** Method that will be invoked if an error is thrown while trying to render a card. */
    onError: PropTypes.func,
    /** JSX styles that will be applied to the card conatiner */
    style: PropTypes.object,
    /** HostConfig. [More Info](https://docs.microsoft.com/en-us/adaptive-cards/rendering-cards/host-config) */
    hostConfig: PropTypes.object,
};

/**
 * AdaptiveCard Module
 * @module AdaptiveCard
 */

/**
 * Default handler for opening a URL action.
 * Opens the specified URL in a new browser tab.
 *
 * @param {AdaptiveCards.OpenUrlAction} action - The action containing the URL to open.
 */

/**
 * Handles the OpenUrlAction by opening the specified URL in a new browser tab.
 *
 * @param {AdaptiveCards.OpenUrlAction} action - The action containing the URL to open.
 */

/**
 * Default handler for opening a URL action.
 * Opens the specified URL in a new browser tab.
 *
 * @param {AdaptiveCards.OpenUrlAction} action - The action containing the URL to open.
 */
const defaultOpenUrlHandler = (action: AdaptiveCards.OpenUrlAction) => {
    window.open(action.url, '_blank');
};

/**
 * Set up the markdown-it markdown processor if it has not been set.
 * This is needed to render markdown text in the adaptive card.
 */
const setUpMarkdownIt = () => {
    if (!AdaptiveCards.AdaptiveCard.onProcessMarkdown) {
        AdaptiveCards.AdaptiveCard.onProcessMarkdown = (text, result) => {
            const md = new MarkdownIt();
            result.outputHtml = md.render(text);
            result.didProcess = true;
        };
    }
};

setUpMarkdownIt();

/**
 * Renders an AdaptiveCard based on the provided payload and configuration.
 * This component manages the lifecycle of an AdaptiveCard instance and handles actions.
 *
 * @param {object} payload - The card schema to render, must comply with the card schema.
 * @param {Function} [onExecuteAction] - Callback invoked when any card action is executed.
 * @param {Function} [onActionSubmit] - Callback invoked when a Submit action is executed.
 * @param {Function} [onActionOpenUrl] - Callback invoked when an Open Url action is executed.
 * @param {Function} [onActionShowCard] - Callback invoked when a Show Card action is executed.
 * @param {Function} [onError] - Callback invoked if an error occurs while rendering the card.
 * @param {object} [style] - CSS styles to apply to the card container.
 * @param {object} [hostConfig] - Configuration for rendering the card.
 * @returns {JSX.Element} A div containing the rendered AdaptiveCard or an error message.
 */

/**
 * Renders an AdaptiveCard based on the provided payload and configuration.
 * This component manages the lifecycle of an AdaptiveCard instance and handles actions.
 *
 * @param {object} payload - The card schema to render, must comply with the card schema.
 * @param {Function} [onExecuteAction] - Callback invoked when any card action is executed.
 * @param {Function} [onActionSubmit] - Callback invoked when a Submit action is executed.
 * @param {Function} [onActionOpenUrl] - Callback invoked when an Open Url action is executed.
 * @param {Function} [onActionShowCard] - Callback invoked when a Show Card action is executed.
 * @param {Function} [onError] - Callback invoked if an error occurs while rendering the card.
 * @param {object} [style] - CSS styles to apply to the card container.
 * @param {object} [hostConfig] - Configuration for rendering the card.
 * @returns {JSX.Element} A div containing the rendered AdaptiveCard or an error message.
 */
export const AdaptiveCard = ({
    payload,
    onExecuteAction,
    onActionSubmit,
    onActionOpenUrl,
    onActionShowCard,
    onError,
    style,
    hostConfig,
}: Props) => {
    const [error, setError] = useState<Error>();
    const targetRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<AdaptiveCards.AdaptiveCard>(
        new AdaptiveCards.AdaptiveCard()
    );
    const executeAction = useCallback(
        (a: AdaptiveCards.Action) => {
            const type = a.getJsonTypeName();
            switch (type) {
                case AdaptiveCards.OpenUrlAction.JsonTypeName: {
                    if (onActionOpenUrl) {
                        onActionOpenUrl(a);
                    } else {
                        defaultOpenUrlHandler(a as AdaptiveCards.OpenUrlAction);
                    }

                    break;
                }
                case AdaptiveCards.ShowCardAction.JsonTypeName: {
                    if (onActionShowCard) {
                        onActionShowCard(a);
                    }
                    break;
                }
                case AdaptiveCards.SubmitAction.JsonTypeName: {
                    if (onActionSubmit) {
                        onActionSubmit(a);
                    }
                    break;
                }
            }
            // handlers for the above cases? Can we simplify to one approach?
            if (onExecuteAction) {
                onExecuteAction(a);
            }
        },
        [onActionOpenUrl, onActionShowCard, onActionSubmit, onExecuteAction]
    );

    useEffect(() => {
        cardRef.current.onExecuteAction = executeAction;
    }, [executeAction]);

    useEffect(() => {
        cardRef.current.hostConfig = new AdaptiveCards.HostConfig(hostConfig);
    }, [hostConfig]);

    useEffect(() => {
        if (!targetRef.current) {
            return;
        }
        const card = cardRef.current;

        try {
            card.parse(payload);
            const result : any = card.render() ;
            const trustedHtml = (typeof window === 'undefined') ? "" : (window.trustedTypes?.emptyHTML ?? "");
            targetRef.current.innerHTML = trustedHtml as string;
            targetRef.current.appendChild(result);
        } catch (cardRenderError) {
            if (onError) {
                onError(cardRenderError);
                setError(cardRenderError);
            }
        }
    }, [hostConfig, payload, onError]);

    if (error) {
        return <div style={{ color: 'red' }}>{error.message}</div>;
    }
    return <div style={style} ref={targetRef} />;
};

AdaptiveCard.propTypes = propTypes;
